import * as React from 'react'
import { isCurrentUserAdminOrGlobalAdminSelector } from '@axilon-apps/domains-identity/state'
import { useSearchParams } from 'react-router-dom'
import {
  useAppSelector,
  useActiveBranch,
  useActiveBranchRecord,
  useGetPlantSiteOptionsQuery
} from '@axilon-apps/domains-ontology-ui-state'
import { dedupeSiteOptions } from './wizard/siteFilter'
import { siteOptionLabel } from './wizard/siteIds'
import { CertTrustNoticeDialog } from './components/CertTrustNoticeDialog'
import type { JobsScope } from './components/JobsTable'
import { useChatAppContext } from '../../chat-app-context'
import { buildIngestAppContextEntries } from './ingest-app-context'
import { INTAKE_PARAM } from './intakes'
import { IngestLanding, type LandingTab } from './wizard/IngestLanding'
import { StepCollect } from './wizard/StepCollect'
import { StepModifyPick } from './wizard/StepModifyPick'
import { StepModifyPlaceholder } from './wizard/StepModifyPlaceholder'
import { StepReview } from './wizard/StepReview'
import { StepRun } from './wizard/StepRun'
import { StepSite } from './wizard/StepSite'
import { StepUpdatePlc } from './wizard/StepUpdatePlc'
import { StepUpdatePss } from './wizard/StepUpdatePss'
import { StepSources } from './wizard/StepSources'
import { WizardPageHeader } from './wizard/WizardChrome'
import { useIngestWizard } from './wizard/useIngestWizard'

const IngestPage = () => {
  // Landing defaults to all branches so historical runs aren't hidden when the
  // active branch has no associated ingest jobs.
  const [scope, setScope] = React.useState<JobsScope>('all')
  const activeBranch = useActiveBranchRecord()
  const wizard = useIngestWizard()
  const isAdmin = useAppSelector(isCurrentUserAdminOrGlobalAdminSelector)
  const showIntakes = isAdmin
  const [searchParams, setSearchParams] = useSearchParams()
  // The URL is the source of truth for the landing tab. Switching tabs
  // replaces the current history entry, so leaving for a job, config, or
  // merge review and pressing Back lands on the tab the user left from.
  const requestedTab = searchParams.get('tab')
  const landingTab: LandingTab =
    requestedTab === 'files'
      ? 'files'
      : requestedTab === 'pss-sync'
        ? 'pss-sync'
        : requestedTab === 'intakes' && showIntakes
          ? 'intakes'
          : requestedTab === 'configs'
            ? 'configs'
            : 'runs'
  const setLandingTab = React.useCallback(
    (tab: LandingTab) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current)
          if (tab === 'runs') next.delete('tab')
          else next.set('tab', tab)
          if (tab !== 'intakes') next.delete(INTAKE_PARAM)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )
  const appliedDeepLink = React.useRef(false)
  const [deepLinkError, setDeepLinkError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (appliedDeepLink.current || searchParams.get('src') !== 'tabular') {
      return
    }
    appliedDeepLink.current = true
    setDeepLinkError(null)
    void wizard
      .startWizardWithSource('tabular', {
        action: searchParams.get('action') === 'build' ? 'build' : 'add',
        configName: searchParams.get('config') ?? ''
      })
      .then(() => {
        setSearchParams(
          (current) => {
            const next = new URLSearchParams(current)
            next.delete('src')
            next.delete('action')
            next.delete('config')
            return next
          },
          { replace: true }
        )
      })
      .catch((error) => {
        setDeepLinkError(error instanceof Error ? error.message : String(error))
      })
  }, [searchParams, setSearchParams, wizard])
  // A site exists on a branch iff its `:Site` node is in that branch's graph.
  // Offer only the active branch's sites: job rows and object-store keys are
  // ingest by-products and would resurface sites from deleted branches. When
  // no branch is pinned the header is omitted and the proxy answers for main.
  const activeBranchHeader = useActiveBranch()
  const { data: plantSites, isLoading: sitesLoading } = useGetPlantSiteOptionsQuery({
    branchId: activeBranchHeader
  })
  const sites = React.useMemo(
    () =>
      dedupeSiteOptions(
        (plantSites ?? []).map((site) => ({
          id: site.id,
          name: siteOptionLabel(site.name, site.id)
        }))
      ),
    [plantSites]
  )

  useChatAppContext(buildIngestAppContextEntries({ scope, tab: landingTab }))

  if (wizard.state.step === 0) {
    return (
      <>
        {deepLinkError ? (
          <div className="mx-auto mb-4 w-full max-w-5xl rounded-lg border border-st-down/30 bg-st-down-bg px-4 py-3 text-sm text-st-down">
            Could not start the tabular ingest: {deepLinkError}
          </div>
        ) : null}
        <IngestLanding
          scope={scope}
          activeBranchId={activeBranch?.id}
          sites={sites}
          showIntakes={showIntakes}
          initialTab={landingTab}
          onTabChange={setLandingTab}
          onScopeChange={setScope}
          onStartWizard={wizard.startWizard}
          drafts={wizard.drafts.map((draft) => ({
            id: draft.draftId,
            siteName: draft.mode === 'new' ? draft.newSiteName : (draft.existingSiteName ?? ''),
            step: draft.step,
            paused: draft.run.paused,
            running:
              draft.queueStarted &&
              !draft.run.paused &&
              Object.values(draft.run.statuses).some(
                (status) => status === 'pending' || status === 'running'
              ),
            updatedAt: draft.updatedAt
          }))}
          onResumeWizard={wizard.resumeDraft}
          onDiscardWizard={wizard.discardDraft}
        />
        <CertTrustNoticeDialog />
      </>
    )
  }

  const inModify = wizard.state.flow === 'modify'
  const pageTitle = inModify ? 'Modify site' : 'New ingest'
  const pageSub = inModify
    ? 'One resource, one action. The wizard runs only the path that action needs.'
    : 'Guided ingest for a site — pick sources, prepare uploads, then launch.'

  return (
    <div className="mx-auto flex w-full max-w-[68rem] flex-col gap-5">
      <WizardPageHeader
        title={pageTitle}
        subtitle={pageSub}
        stepCounter={`Step 0${wizard.state.step} of 04`}
        onBackToList={wizard.goList}
      />

      <div
        key={`${wizard.state.step}-${wizard.state.view}-${wizard.state.modStage ?? ''}`}
        className="animate-in fade-in-0 slide-in-from-bottom-1 duration-200 motion-reduce:animate-none"
      >
        {wizard.state.step === 1 ? (
          <StepSite wizard={wizard} sitesLoading={sitesLoading} sites={sites} />
        ) : null}

        {wizard.state.step === 2 &&
        wizard.state.flow === 'modify' &&
        wizard.state.modStage === 'pick' ? (
          <StepModifyPick wizard={wizard} />
        ) : null}

        {wizard.state.step === 2 && wizard.state.flow === 'new' && wizard.state.view === 'form' ? (
          <StepSources wizard={wizard} />
        ) : null}

        {wizard.state.step === 2 &&
        wizard.state.flow === 'new' &&
        wizard.state.view === 'review' ? (
          <StepReview wizard={wizard} />
        ) : null}

        {wizard.state.step === 3 &&
        (wizard.state.flow === 'new' || wizard.state.modStage === 'collect') ? (
          <StepCollect wizard={wizard} />
        ) : null}

        {wizard.state.step === 3 && wizard.state.modStage === 'plcupdate' ? (
          <StepUpdatePlc wizard={wizard} />
        ) : null}

        {wizard.state.step === 3 && wizard.state.modStage === 'pssupdate' ? (
          <StepUpdatePss wizard={wizard} />
        ) : null}

        {wizard.state.step === 3 &&
        (wizard.state.modStage === 'pickupdate' ||
          wizard.state.modStage === 'remove' ||
          wizard.state.modStage === 'docmap') ? (
          <StepModifyPlaceholder wizard={wizard} />
        ) : null}

        {wizard.state.step === 4 ? <StepRun wizard={wizard} /> : null}
      </div>

      <CertTrustNoticeDialog />
    </div>
  )
}

export default IngestPage
